import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { areChartAnimationsDisabled } from "@/utils/chartAnimation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatNumber } from "@/utils/numberFormatter";
import { FootprintProduct, FootprintConsumption } from "@/types/results";

interface MaterialBreakdownChartProps {
  product: FootprintProduct | null;
  consumptions: FootprintConsumption[];
  type: "carbon" | "hydric";
  className?: string;
}

interface ChartDataItem {
  name: string;
  value: number;
  id: string;
  materialName?: string;
  quantity: number;
  unit: string;
}

export const MaterialBreakdownChart = ({ product, consumptions, type, className }: MaterialBreakdownChartProps) => {
  const { t } = useLanguage();

  // Early return if no product
  if (!product) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {type === "carbon" ? t("results.material.carbon.title") : t("results.material.hydric.title")}
          </CardTitle>
          <p className="text-sm text-gray-600">
            {type === "carbon" ? t("results.material.carbon.description") : t("results.material.hydric.description")}
          </p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <p>{t("results.material.noData")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Filter consumptions to only materials for this product's footprint
  const materialConsumptions = consumptions.filter(
    (consumption) => consumption.footprint_id === product.footprint_id && consumption.consumption_type === "material"
  );

  // Create chart data by matching materials to their consumption footprints
  const chartData: ChartDataItem[] =
    product.materials_used?.map((materialUsed, index) => {
      // Match by setup_item_id or setup_item_name
      const matchingConsumption = materialConsumptions.find(
        (consumption) =>
          consumption.setup_item_id === materialUsed.material_id ||
          consumption.setup_item_name === materialUsed.materialName
      );

      // Use the footprint directly
      const footprintValue = matchingConsumption 
        ? (type === "carbon" ? matchingConsumption.carbon_footprint : matchingConsumption.hydric_footprint)
        : 0;

      return {
        name: materialUsed.materialName || `Material ${index + 1}`,
        value: footprintValue,
        id: materialUsed.material_id || `material-${index}`,
        materialName: materialUsed.materialName,
        quantity: materialUsed.quantity || 0,
        unit: materialUsed.unit || "",
      };
    }) || [];

  // Sort by value descending
  chartData.sort((a: ChartDataItem, b: ChartDataItem) => b.value - a.value);

  // Calculate total
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const colors = [
    "#14b8a6", // teal-500
    "#0891b2", // cyan-600
    "#f59e0b", // amber-500
    "#8b5cf6", // violet-500
    "#ef4444", // red-500
    "#10b981", // emerald-500
    "#f97316", // orange-500
    "#6366f1", // indigo-500
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded-md shadow-sm">
          <p className="text-sm font-medium mb-1">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-600">
                {type === "carbon" ? t("results.carbon.units.kgCO2e") : t("results.hydric.units.m3")}:
              </span>
              <span className="text-sm font-medium">{formatNumber(data.value)}</span>
            </div>
            {data.quantity && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-600">{t("results.material.quantity")}:</span>
                <span className="text-sm font-medium">
                  {formatNumber(data.quantity)} {data.unit}
                </span>
              </div>
            )}
            {data.materialName && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-600">Material:</span>
                <span className="text-sm font-medium">{data.materialName}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // Show message if no data
  if (chartData.length === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {type === "carbon" ? t("results.material.carbon.title") : t("results.material.hydric.title")}
          </CardTitle>
          <p className="text-sm text-gray-600">
            {type === "carbon" ? t("results.material.carbon.description") : t("results.material.hydric.description")}
          </p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <p>{t("results.material.noData")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">
          {type === "carbon" ? t("results.material.carbon.title") : t("results.material.hydric.title")}
        </CardTitle>
        <p className="text-sm text-gray-600">
          {type === "carbon" ? t("results.material.carbon.description") : t("results.material.hydric.description")}
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[350px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} textAnchor="middle" height={40} />
              <YAxis
                tick={{ fontSize: 12 }}
                label={{
                  value: type === "carbon" ? `${t("results.carbon.units.kgCO2e")}` : `${t("results.hydric.units.m3")}`,
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={!areChartAnimationsDisabled()}>
                {chartData.map((_, index: number) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">{t("results.material.total")}:</span>
            <span className="text-lg font-bold">
              {formatNumber(total)}{" "}
              {type === "carbon" ? t("results.carbon.units.kgCO2e") : t("results.hydric.units.m3")}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
